import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Eye, Heart, MessageCircle, Share, TrendingUp } from 'lucide-react';
import api from '../../services/api';

interface PostAnalyticsProps {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
}

const PostAnalytics: React.FC<PostAnalyticsProps> = ({ postId, isOpen, onClose }) => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && postId) {
      loadAnalytics();
    }
  }, [isOpen, postId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/posts/${postId}/analytics`);
      if (response.data?.success) {
        setAnalytics(response.data.data);
      } else {
        // Mock analytics data
        setAnalytics({
          totalViews: 1247,
          totalLikes: 89,
          totalComments: 23,
          totalShares: 12,
          viewsOverTime: [
            { date: '2024-01-01', views: 45 },
            { date: '2024-01-02', views: 78 },
            { date: '2024-01-03', views: 123 },
            { date: '2024-01-04', views: 156 },
            { date: '2024-01-05', views: 189 },
            { date: '2024-01-06', views: 234 },
            { date: '2024-01-07', views: 267 }
          ],
          engagementBreakdown: [
            { name: 'Likes', value: 89, color: '#ef4444' },
            { name: 'Comments', value: 23, color: '#3b82f6' },
            { name: 'Shares', value: 12, color: '#10b981' }
          ],
          demographics: {
            ageGroups: [
              { age: '18-24', count: 45 },
              { age: '25-34', count: 67 },
              { age: '35-44', count: 34 },
              { age: '45+', count: 21 }
            ],
            sports: [
              { sport: 'Football', count: 78 },
              { sport: 'Cricket', count: 45 },
              { sport: 'Tennis', count: 34 },
              { sport: 'Chess', count: 23 }
            ]
          }
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Post Analytics</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading analytics...</p>
          </div>
        ) : analytics ? (
          <div className="p-6 space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">Views</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-1">{analytics.totalViews.toLocaleString()}</p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Heart className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-600">Likes</span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-1">{analytics.totalLikes}</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Comments</span>
                </div>
                <p className="text-2xl font-bold text-green-900 mt-1">{analytics.totalComments}</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Share className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-600">Shares</span>
                </div>
                <p className="text-2xl font-bold text-purple-900 mt-1">{analytics.totalShares}</p>
              </div>
            </div>

            {/* Views Over Time */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Views Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.viewsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="views" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Engagement Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Breakdown</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analytics.engagementBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {analytics.engagementBreakdown.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Audience by Sport</h3>
                <div className="space-y-3">
                  {analytics.demographics.sports.map((sport: any) => (
                    <div key={sport.sport} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{sport.sport}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(sport.count / 78) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{sport.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No analytics data available
          </div>
        )}
      </div>
    </div>
  );
};

export default PostAnalytics;